<template>
  <page-loader>
    <editor-toolbar
      :is-loading="isLoading"
      :item="partner.partner.default"
      :slug="partner.slug"
      type="link"
      :is-archived="partner.archived"
    />
    <v-divider />
    <v-container>
      <v-row>
        <v-col>
          <empty-list v-if="!offerDataMappedToLinks.length" type="linked offer" />
          <v-card v-else>
            <v-tabs v-model="tab" vertical>
              <v-tab v-for="offer in offerDataMappedToLinks" :key="offer.slug" class="justify-start text-left">
                <div class="offer-tab d-flex justify-space-between align-center">
                  <div>
                    <div>{{ offer.offer.default.name }}</div>
                    <div class="overline font-weight-bold line-height-1">
                      {{ formatDiffKey('offerTypes', offer.type) }}
                    </div>
                  </div>
                  <published-chip class="ml-1" :value="offer.offer.default.published" label no-text small />
                </div>
              </v-tab>
              <v-tabs-items v-model="tab" class="pa-2" style="min-width:0">
                <offer-block
                  v-model="linksUpdated[tab]"
                  :is-disabled="!isPermittedTo('update')"
                  :offer="offerDataMappedToLinks[tab]"
                  :partner="partner"
                  :link-status-stats="linkStatsForOffer(offerDataMappedToLinks[tab])"
                />
              </v-tabs-items>
            </v-tabs>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <fab-actions
      v-if="!hasInvalidLinks"
      :can-edit="canUpdate"
      :has-changes="hasChanges"
      context="Links"
      @reset="showResetModal = true"
      @save="showSaveModal = true"
    />
    <fab-error v-if="hasInvalidLinks" @click="onErrorFabClick" />
    <generic-modal v-model="showErrorModal" title="Affiliate Link Errors" hide-buttons hide-louis>
      <template #subtitle>
        <div>
          Affiliate links <span class="font-weight-bold">MUST</span> begin with <code>https://</code>. If this format is
          not followed, you'll be unable to save any changes. The URLs below are not following this format.
        </div>
      </template>
      <data-table :headers="invalidLinkHeaders" :items="invalidLinks" hide-default-footer>
        <template #item.offer="{ item }">
          {{ getOfferNameFromSlug(item.offer) }}
        </template>
        <template #item.country="{ item }">
          {{ formattedOption('countries', item.country) }}
        </template>
        <template #item.language="{ item }">
          {{ formattedOption('translationLanguages', item.language) }}
        </template>
      </data-table>
    </generic-modal>
    <save-modal v-model="showSaveModal" :is-disabled="isLoading" :is-loading="isLoading" @modal-confirm="onSaveConfirm">
      <v-list>
        <v-list-item v-for="offer in diffNormalised" :key="offer.title" dense>
          <v-list-item-content>
            <v-list-item-title class="subtitle-1 font-weight-bold" v-text="offer.title" />
            <v-list-item-subtitle>
              <div v-for="(changes, country) in offer.countries" :key="`${offer.title}-${country}`" class="mb-2">
                {{ country }}
                <diff-item
                  v-for="(change, index) in changes"
                  :key="`${offer.title}-${index}-${change.context}`"
                  :change="change"
                />
              </div>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </save-modal>
    <reset-modal
      v-model="showResetModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      category="links"
      @modal-confirm="onResetConfirm"
    />
    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
  </page-loader>
</template>

<script>
// Helpers
import Vue from 'vue'
import { mapGetters, mapMutations } from 'vuex'
import delay from '@/utils/delay'
// Components
import DataTable from '@/components/data-table'
import DiffItem from '@/components/object-diff/diff-item'
import EditorToolbar from '@/components/editor-toolbar'
import EmptyList from '@/components/empty-list'
import FabActions from '@/components/fab-actions'
import FabError from '@/components/fab-error'
import OfferBlock from '@/views/links/components/offer-block'
import PageLoader from '@/components/page-loader'
import PublishedChip from '@/components/chips/published'
import GenericModal from '@/components/modals/generic-modal'
import ResetModal from '@/components/modals/reset-modal'
import SaveModal from '@/components/modals/save-modal'
// Mixins
import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
// Services
import { languages, links, linkStatus, partners } from '@/services/offers'

export default {
  name: 'LinksEditor',
  components: {
    DataTable,
    DiffItem,
    EditorToolbar,
    EmptyList,
    FabActions,
    FabError,
    GenericModal,
    OfferBlock,
    PageLoader,
    PublishedChip,
    ResetModal,
    SaveModal,
  },
  mixins: [DataModification, PermissionsHandler],
  data: () => ({
    isLoading: false,
    partner: { partner: { default: {} } },
    languageOptions: [],
    linkedOffers: [],
    linksOriginal: [],
    linksUpdated: [],
    linkStatusStats: [],
    tab: 0,
    showSaveModal: false,
    showResetModal: false,
    showErrorModal: false,
    invalidLinkHeaders: [
      { text: 'Offer', value: 'offer' },
      { text: 'Country', value: 'country' },
      { text: 'Language', value: 'language' },
    ],
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['getItemsForOption', 'options', 'formattedOption']),
    offerDataMappedToLinks() {
      return this.linksOriginal.map(({ offer }) => this.linkedOffers.find(({ slug }) => slug === offer)).filter(Boolean)
    },
    diff() {
      if (!this.getDiff(this.linksOriginal, this.linksUpdated)) return
      return this.linksUpdated
        .map((group, index) => {
          const diff = this.getDiff(this.linksOriginal.length ? this.linksOriginal[index].links : {}, group.links)
          if (!diff) return
          return {
            index,
            diff,
          }
        })
        .filter(Boolean)
    },
    diffNormalised() {
      return (
        this.diff &&
        this.diff.reduce((diffArray, { index, diff }) => {
          const link = this.linksUpdated[index]
          const title = this.diffTitle(link)
          const countries = diff.reduce(this.groupDiffsByCountry, {})
          const { offer, partner } = link

          return diffArray.concat([{ title, countries, offer, partner }])
        }, [])
      )
    },
    invalidLinks() {
      return this.linksUpdated.flatMap(({ links, offer }) => {
        return Object.entries(links).flatMap(([country, urls]) => {
          const invalid = Object.entries(urls)
            .filter(([, url]) => url && !url.toLowerCase().startsWith('https://'))
            .map(([language, url]) => ({ offer, country, language, url }))

          return invalid
        })
      })
    },
    hasInvalidLinks() {
      return this.invalidLinks.length
    },
    hasChanges() {
      return !!this.diff
    },
    canUpdate() {
      return this.isPermittedTo('update') && !this.hasInvalidLinks
    },
    isDisabled() {
      return this.isLoading || this.hasPageErrors || !this.isPermittedTo('update')
    },
  },
  async created() {
    this.setBeforeUnloadListener(this.onBeforeUnload)

    await Promise.all([this.fetchLanguageOptions(), this.getLinksData()])
    this.setPageLoading(false)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    linkStatsForOffer({ slug }) {
      return this.linkStatusStats.filter(({ offer }) => offer === slug)
    },
    async fetchLanguageOptions() {
      this.isLoading = true
      try {
        this.languageOptions = (await languages.getLanguages()).map(({ name, code }) => ({ key: name, value: code }))
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch translation language options',
          duration: -1,
          data: { error },
        })
      }
      this.isLoading = false
    },
    async getLinksData() {
      try {
        const [linkData, linkStatusStats, partner, linkedOffers] = await Promise.all([
          links.getLinksForPartner(this.$route.params.slug),
          linkStatus.getLinkStats(this.$route.params.slug),
          partners.getSinglePartner(this.$route.params.slug),
          partners.getOffersForPartner(this.$route.params.slug),
        ])
        this.partner = partner
        this.linkStatusStats = linkStatusStats
        this.linkedOffers = linkedOffers

        /* Some offers unlinked from a partner still appear in link data and need filtering out. */
        const filteredLinkData = linkData.filter(({ offer }) => this.linkedOffers.find(({ slug }) => slug === offer))
        this.linksOriginal = this.clone(filteredLinkData)
        this.linksUpdated = this.clone(filteredLinkData)
      } catch (errors) {
        console.log(errors)
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to get links',
        })
      }
    },
    async onSaveConfirm() {
      this.isLoading = true
      try {
        const updatedLinks = this.linksUpdated.filter(group =>
          this.diffNormalised.find(diff => diff.offer === group.offer)
        )
        await Promise.all(updatedLinks.map(links.updateLinksForPartner))
        await delay(500)

        this.$notify({
          type: 'success',
          text: 'Links updated successfully',
        })
        await this.getLinksData()
        this.showSaveModal = false
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save links',
        })
      }

      this.isLoading = false
    },
    onResetConfirm() {
      this.showResetModal = false
      Vue.set(this, 'linksUpdated', this.clone(this.linksOriginal))
    },
    diffTitle(link) {
      if (!link) return
      const offer = this.linkedOffers.find(({ slug }) => slug === link.offer)
      if (!offer) return
      const {
        offer: {
          default: { name },
        },
        type,
      } = offer

      return `${name} - ${this.formatDiffKey('offerTypes', type)}`
    },
    getCountryNameByCode(countryCode) {
      return this.languageOptions.find(({ value }) => value === countryCode)?.key || countryCode
    },
    getOfferNameFromSlug(slug) {
      const found = this.linkedOffers.find(offer => offer.slug === slug)
      if (!found) return slug
      const offerType = this.formattedOption('offerTypes', found.type)
      return `${found.offer.default.name} (${offerType})`
    },
    groupDiffsByCountry(countries, { item, kind, path, lhs, rhs }) {
      const type = this.getDiffType({ kind, item, lhs, rhs })
      const [countryKey, language] = path

      const country = this.formatDiffKey('countries', countryKey)

      const change = {
        context: this.getCountryNameByCode(language),
        type,
        value: this.getDiffValue({ item, lhs, rhs }, type),
      }

      if (countries[country]) {
        countries[country].push(change)
      } else {
        countries[country] = [change]
      }

      return countries
    },
    onErrorFabClick() {
      this.showErrorModal = true
    },
  },
}
</script>

<style lang="scss" scoped>
.offer-tab {
  width: 100%;
}
</style>

<style>
.line-height-1 {
  line-height: 1 !important;
}
</style>
